import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  useToast,
  Text,
  Box,
  Badge,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Flex,
} from "@chakra-ui/react";
import { useState, useEffect, useCallback } from "react";
import { productService } from "@/lib/services/productService";

interface Product {
  id: number;
  product_name: string;
  serial_no: string;
  quantity: number;
  threshold: number;
  status: string;
}

interface ReplenishModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  products?: Product[];
}

interface ProductRestock {
  product: Product;
  selected: boolean;
  quantityToAdd: string;
}

const ReplenishModal = ({
  isOpen,
  onClose,
  onSuccess,
  products = [],
}: ReplenishModalProps) => {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [productRestocks, setProductRestocks] = useState<ProductRestock[]>([]);

  // Filter products that need replenishment - memoize to prevent unnecessary re-renders
  const lowStockProducts = products.filter(
    (p) => p.status === "Need to replenish",
  );

  // Initialize state when modal opens - use useCallback for stable function
  const initializeRestocks = useCallback(() => {
    return lowStockProducts.map((product) => ({
      product,
      selected: true,
      quantityToAdd: "",
    }));
  }, [lowStockProducts]);

  // Initialize only once when modal opens
  useEffect(() => {
    if (isOpen && productRestocks.length === 0) {
      setProductRestocks(initializeRestocks());
    }

    // Reset when modal closes
    if (!isOpen) {
      // Use setTimeout to avoid state update during render
      const timer = setTimeout(() => {
        setProductRestocks([]);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, initializeRestocks]);

  const handleToggleProduct = useCallback((index: number): void => {
    setProductRestocks((prev) => {
      const newState = [...prev];
      if (newState[index]) {
        newState[index] = {
          ...newState[index],
          selected: !newState[index].selected,
        };
      }
      return newState;
    });
  }, []);

  const handleQuantityChange = useCallback(
    (index: number, value: string): void => {
      // Allow empty string or valid numbers
      const numValue = parseInt(value);
      if (
        value === "" ||
        (!isNaN(numValue) && numValue > 0 && numValue <= 10000)
      ) {
        setProductRestocks((prev) => {
          const newState = [...prev];
          if (newState[index]) {
            newState[index] = {
              ...newState[index],
              quantityToAdd: value,
            };
          }
          return newState;
        });
      }
    },
    [],
  );

  const handleReplenish = async (): Promise<void> => {
    const selectedItems = productRestocks.filter(
      (item) =>
        item.selected && item.quantityToAdd && parseInt(item.quantityToAdd) > 0,
    );

    if (selectedItems.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one product and enter quantity",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      // Prepare data for bulk replenish
      const replenishData = selectedItems.map((item) => ({
        id: item.product.id,
        quantity: parseInt(item.quantityToAdd),
      }));

      // Use the productService method
      const result = await productService.bulkReplenishStock(replenishData);

      if (result.success) {
        toast({
          title: "Success",
          description: result.message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Call the success callback if provided
        if (onSuccess) {
          onSuccess();
        }

        onClose();
      } else {
        if (result.data?.successful?.length > 0) {
          toast({
            title: "Partial Success",
            description: `${result.data.successful.length} product(s) updated, ${result.data.failed?.length || 0} failed`,
            status: "warning",
            duration: 4000,
            isClosable: true,
          });

          if (onSuccess) {
            onSuccess();
          }

          onClose();
        } else {
          throw new Error(result.message || "Failed to replenish stock");
        }
      }
    } catch (error: any) {
      console.error("Replenish error:", error);

      // Check if it's a 404 error (endpoint doesn't exist)
      if (
        error.message.includes("404") ||
        error.message.includes("Not Found")
      ) {
        toast({
          title: "Backend Error",
          description:
            "The bulk replenish endpoint is not available. Please check your backend routes.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: error.message || "Failed to replenish stock",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const selectedCount = productRestocks.filter((item) => item.selected).length;
  const totalSelected = productRestocks.filter(
    (item) =>
      item.selected && item.quantityToAdd && parseInt(item.quantityToAdd) > 0,
  ).length;

  // Calculate total quantity to add
  const totalQuantityToAdd = productRestocks
    .filter((item) => item.selected && item.quantityToAdd)
    .reduce((sum, item) => sum + parseInt(item.quantityToAdd || "0"), 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "full", sm: "md", md: "lg", xl: "xl" }}
      closeOnOverlayClick={!isLoading}
      isCentered
      scrollBehavior="inside"
      onCloseComplete={() => {
        // Reset state after modal fully closes
        setTimeout(() => setProductRestocks([]), 100);
      }}
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent
        borderRadius={{ base: 0, md: "none" }}
        mx={{ base: 0, md: 4 }}
        maxH={{ base: "100vh", md: "90vh" }}
      >
        <ModalHeader
          fontSize={{ base: "lg", md: "xl" }}
          py={{ base: 3, md: 4 }}
          px={{ base: 4, md: 6 }}
        >
          Replenish Stocks
        </ModalHeader>
        <ModalCloseButton
          isDisabled={isLoading}
          size="sm"
          top={{ base: 3, md: 4 }}
          right={{ base: 4, md: 6 }}
          variant="link"
          color="#0A1B39"
          _hover={{ color: "#DC3611" }}
          _focus={{ boxShadow: "none" }}
        />
        <ModalBody
          pb={{ base: 4, md: 6 }}
          px={{ base: 4, md: 6 }}
          overflowY="auto"
        >
          <VStack spacing={{ base: 3, md: 4 }} align="stretch">
            <Divider borderColor="#E7E8EB" my={{ base: -2, md: 0 }} />

            {/* Header Info */}
            <Box
              p={{ base: 2, md: 3 }}
              bg="blue.50"
              borderColor="blue.200"
              borderRadius="md"
              fontSize={{ base: "xs", md: "sm" }}
            >
              <Text color="blue.800">
                📦 {lowStockProducts.length} product(s) need restocking •{" "}
                {selectedCount} selected
              </Text>
            </Box>

            {lowStockProducts.length === 0 ? (
              <Box textAlign="center" py={{ base: 6, md: 8 }}>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.900"
                  fontWeight="600"
                >
                  No products need restocking!
                </Text>
                <Text
                  fontSize={{ base: "xs", md: "sm" }}
                  color="gray.400"
                  mt={2}
                >
                  All products are above their threshold levels
                </Text>
              </Box>
            ) : (
              <>
                {/* Product List */}
                <VStack spacing={{ base: 2, md: 3 }} align="stretch">
                  {productRestocks.map((item, index) => {
                    const newQuantity = item.quantityToAdd
                      ? item.product.quantity + parseInt(item.quantityToAdd)
                      : item.product.quantity;
                    const willBeGood = newQuantity > item.product.threshold;

                    return (
                      <Box
                        key={`product-${item.product.id}-${index}`}
                        p={{ base: 3, md: 4 }}
                        bg={item.selected ? "white" : "gray.50"}
                        borderRadius="md"
                        borderWidth="2px"
                        borderColor={item.selected ? "blue.300" : "gray.200"}
                        _hover={{ borderColor: "blue.200" }}
                      >
                        <Stack
                          direction={{ base: "column", sm: "row" }}
                          align={{ base: "stretch", sm: "start" }}
                          spacing={3}
                        >
                          {/* Checkbox */}
                          <Checkbox
                            isChecked={item.selected}
                            onChange={() => handleToggleProduct(index)}
                            colorScheme="blue"
                            isDisabled={isLoading}
                            size={{ base: "sm", md: "md" }}
                            alignSelf="flex-start"
                          />

                          {/* Product Info */}
                          <Box flex={1}>
                            <VStack align="stretch" spacing={2}>
                              <Flex
                                justify="space-between"
                                align={{ base: "flex-start", sm: "center" }}
                                direction={{ base: "column", sm: "row" }}
                                gap={2}
                              >
                                <Text
                                  fontWeight="bold"
                                  fontSize={{ base: "xs", md: "sm" }}
                                  noOfLines={2}
                                >
                                  {item.product.product_name}
                                </Text>
                                <Badge
                                  colorScheme={
                                    item.product.quantity <=
                                    item.product.threshold
                                      ? "orange"
                                      : "green"
                                  }
                                  fontSize="xs"
                                  alignSelf={{
                                    base: "flex-start",
                                    sm: "center",
                                  }}
                                >
                                  {item.product.quantity} units
                                </Badge>
                              </Flex>

                              <Flex
                                direction={{ base: "column", sm: "row" }}
                                fontSize={{ base: "2xs", sm: "xs" }}
                                color="gray.600"
                                gap={1}
                              >
                                <Text>S/N: {item.product.serial_no}</Text>
                                <Text display={{ base: "none", sm: "block" }}>
                                  •
                                </Text>
                                <Text>Threshold: {item.product.threshold}</Text>
                              </Flex>

                              {/* Quantity Input */}
                              {item.selected && (
                                <FormControl>
                                  <FormLabel
                                    fontSize={{ base: "2xs", sm: "xs" }}
                                    mb={1}
                                  >
                                    Add Quantity (1-10,000)
                                  </FormLabel>
                                  <Input
                                    type="number"
                                    min="1"
                                    max="10000"
                                    value={item.quantityToAdd}
                                    onChange={(e) =>
                                      handleQuantityChange(
                                        index,
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Enter quantity"
                                    size="sm"
                                    bg="white"
                                    isDisabled={isLoading}
                                    h={{ base: "36px", md: "auto" }}
                                    fontSize={{ base: "xs", md: "sm" }}
                                  />
                                </FormControl>
                              )}

                              {/* Preview */}
                              {item.selected &&
                                item.quantityToAdd &&
                                parseInt(item.quantityToAdd) > 0 && (
                                  <Box
                                    p={{ base: 2, md: 2 }}
                                    bg={willBeGood ? "green.50" : "yellow.50"}
                                    borderRadius="md"
                                    fontSize={{ base: "2xs", sm: "xs" }}
                                  >
                                    <Flex
                                      justify="space-between"
                                      align="center"
                                      mb={1}
                                    >
                                      <Text color="gray.700">
                                        New quantity:
                                      </Text>
                                      <Badge
                                        colorScheme={
                                          willBeGood ? "green" : "yellow"
                                        }
                                        fontSize="xs"
                                      >
                                        {newQuantity} units
                                      </Badge>
                                    </Flex>
                                    <Flex
                                      justify="space-between"
                                      align="center"
                                    >
                                      <Text color="gray.700">Status:</Text>
                                      <Badge
                                        colorScheme={
                                          willBeGood ? "green" : "yellow"
                                        }
                                        fontSize="xs"
                                      >
                                        {willBeGood ? "GOOD" : "STILL LOW"}
                                      </Badge>
                                    </Flex>
                                  </Box>
                                )}
                            </VStack>
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}
                </VStack>

                <Divider />

                {/* Summary */}
                {totalSelected > 0 && (
                  <Box
                    p={{ base: 2, md: 3 }}
                    bg="gray.50"
                    borderRadius="md"
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    <Text fontWeight="bold" mb={2}>
                      Summary
                    </Text>
                    <VStack align="stretch" spacing={1}>
                      <Text color="gray.600">
                        {totalSelected} product(s) ready to restock
                      </Text>
                      <Text color="gray.600">
                        Total quantity to add: {totalQuantityToAdd} units
                      </Text>
                    </VStack>
                  </Box>
                )}
              </>
            )}

            {/* Action Buttons */}
            <Stack
              direction={{ base: "column", sm: "row" }}
              justify="flex-end"
              spacing={{ base: 2, md: 3 }}
              pt={2}
              w="full"
            >
              <Button
                variant="outline"
                onClick={onClose}
                isDisabled={isLoading}
                size={{ base: "sm", md: "md" }}
                w={{ base: "full", sm: "auto" }}
                fontSize={{ base: "xs", md: "sm" }}
              >
                Cancel
              </Button>
              <Button
                bg="#DC3611"
                _hover={{ bg: "#bf3c1fff" }}
                color="white"
                onClick={handleReplenish}
                isLoading={isLoading}
                loadingText="Processing..."
                isDisabled={
                  totalSelected === 0 ||
                  lowStockProducts.length === 0 ||
                  isLoading
                }
                size={{ base: "sm", md: "md" }}
                w={{ base: "full", sm: "auto" }}
                fontSize={{ base: "xs", md: "sm" }}
              >
                Replenish {totalSelected > 0 && `(${totalSelected})`}
              </Button>
            </Stack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ReplenishModal;
